import React from 'react'
import { Link } from 'react-router-dom'
import PublicFooter from '../ui/PublicFooter'
import { api } from '../lib/api'

export default function ForgotPasswordPage() {
  const [orgOptions, setOrgOptions] = React.useState([])
  const [loadingOrgs, setLoadingOrgs] = React.useState(false)

  const [orgCategory, setOrgCategory] = React.useState('') // GA | INSURER
  const [orgL1, setOrgL1] = React.useState('') // 1차 조직 id (회사)
  const [orgBranch, setOrgBranch] = React.useState('') // 하위 지사 id (최종 선택)
  const [name, setName] = React.useState('')
  const [phoneNumber, setPhoneNumber] = React.useState('')
  const [email, setEmail] = React.useState('')

  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [done, setDone] = React.useState(false)

  React.useEffect(() => {
    let ignore = false
    ;(async () => {
      setLoadingOrgs(true)
      try {
        const res = await api.get('/orgs/public-options')
        const data = res?.data?.data ?? res?.data ?? []
        if (!ignore) setOrgOptions(Array.isArray(data) ? data : [])
      } catch {
        if (!ignore) setOrgOptions([])
      } finally {
        if (!ignore) setLoadingOrgs(false)
      }
    })()
    return () => { ignore = true }
  }, [])

  const onSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setDone(false)

    if (!orgCategory) return setError('소속 구분을 선택해 주세요.')
    const list = Array.isArray(orgOptions) ? orgOptions : []
    const roots = list.filter(o => o && o.parentOrgId == null)
    const personalRoot = roots.find(o => String(o.type).toUpperCase() === 'PERSONAL')
    const effectiveOrgId = orgCategory === 'PERSONAL'
      ? (personalRoot?.id != null ? Number(personalRoot.id) : null)
      : (orgL1 && orgBranch ? Number(orgBranch) : null)
    if (orgCategory !== 'PERSONAL' && (!orgL1 || !orgBranch)) return setError('소속을 선택해 주세요. (구분 → 1차 조직 → 하위 지사)')
    if (orgCategory === 'PERSONAL' && effectiveOrgId == null) return setError('개인 회원 정보를 불러오는 중입니다. 잠시 후 다시 시도해 주세요.')
    if (!name) return setError('이름을 입력해 주세요.')
    if (!phoneNumber) return setError('휴대폰 번호를 입력해 주세요.')
    if (!email) return setError('이메일을 입력해 주세요.')

    setBusy(true)
    try {
      await api.post('/auth/password/reset/request', { orgId: effectiveOrgId, name, phoneNumber, email })
      // 서버는 항상 ok=true를 주므로, UX도 "발송 안내"로 통일
      setDone(true)
    } catch {
      setError('요청에 실패했습니다. 잠시 후 다시 시도해 주세요.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm space-y-4 p-6 border rounded-lg bg-white">
          <h1 className="text-xl font-semibold">비밀번호 찾기</h1>
          <p className="text-xs text-slate-500">
            입력한 정보가 계정과 일치하면, 알림톡과 이메일로 임시 비밀번호가 발송됩니다.
          </p>

          {done && (
            <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
              요청이 접수되었습니다. 알림톡 또는 메일함을 확인해 주세요.
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-3">
            {error && <div className="text-red-600 text-sm">{error}</div>}

            <div>
              <label className="block text-sm text-slate-600">소속</label>
              <div className="mt-1 grid grid-cols-1 gap-2">
                <select
                  className="w-full rounded border px-3 py-2"
                  value={orgCategory}
                  onChange={(e) => {
                    const v = e.target.value
                    setOrgCategory(v)
                    setOrgL1('')
                    setOrgBranch('')
                  }}
                  disabled={loadingOrgs || busy}
                  required
                >
                  <option value="">{loadingOrgs ? '불러오는 중…' : '구분 선택'}</option>
                  <option value="GA">GA</option>
                  <option value="INSURER">원수사</option>
                  <option value="PERSONAL">개인회원</option>
                </select>

                {orgCategory && orgCategory !== 'PERSONAL' && (
                <>
                <select
                  className="w-full rounded border px-3 py-2"
                  value={orgL1}
                  onChange={(e) => {
                    setOrgL1(e.target.value)
                    setOrgBranch('')
                  }}
                  disabled={loadingOrgs || busy}
                  required={orgCategory !== 'PERSONAL'}
                >
                  <option value="">{orgCategory ? '1차 조직 선택' : '먼저 구분을 선택하세요'}</option>
                  {(() => {
                    const list = Array.isArray(orgOptions) ? orgOptions : []
                    const byId = {}
                    list.forEach(o => { if (o?.id != null) byId[o.id] = o })

                    const roots = list.filter(o => o && o.parentOrgId == null)
                    const insurerRoots = roots.filter(o => String(o.type).toUpperCase() === 'INSURER')
                    const insurerRootIds = new Set(insurerRoots.map(o => o.id))

                    const isCompanyNode = (o) => {
                      if (!o || String(o.type).toUpperCase() !== 'INSURER') return false
                      const p = o.parentOrgId != null ? byId[o.parentOrgId] : null
                      if (!p) return false
                      const gp = p.parentOrgId != null ? byId[p.parentOrgId] : null
                      return !gp
                    }

                    let l1 = []
                    if (orgCategory === 'GA') {
                      l1 = roots.filter(o => String(o.type).toUpperCase() === 'GA')
                    } else if (orgCategory === 'INSURER') {
                      // "손해보험/생명보험" 같은 루트 대신 회사 노드(삼성화재 등)를 1차로 노출
                      l1 = list
                        .filter(isCompanyNode)
                        .filter(o => insurerRootIds.has(o.parentOrgId))
                    } else if (orgCategory === 'PERSONAL') {
                      l1 = roots.filter(o => String(o.type).toUpperCase() === 'PERSONAL')
                    }

                    l1 = l1.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
                    return l1.map((o) => (
                      <option key={String(o.id)} value={String(o.id)}>
                        {o.name}
                      </option>
                    ))
                  })()}
                </select>

                <select
                  className="w-full rounded border px-3 py-2"
                  value={orgBranch}
                  onChange={(e) => setOrgBranch(e.target.value)}
                  disabled={loadingOrgs || busy || !orgCategory || !orgL1}
                  required
                >
                  <option value="">{orgL1 ? '하위 지사 선택' : '먼저 1차 조직을 선택하세요'}</option>
                  {(() => {
                    const list = Array.isArray(orgOptions) ? orgOptions : []
                    const byId = {}
                    list.forEach(o => { if (o?.id != null) byId[o.id] = o })

                    const parentToChildren = new Map()
                    list.forEach(o => {
                      if (!o || o.parentOrgId == null || o.id == null) return
                      const key = String(o.parentOrgId)
                      const arr = parentToChildren.get(key) || []
                      arr.push(o)
                      parentToChildren.set(key, arr)
                    })

                    const makePath = (id) => {
                      const names = []
                      const seen = new Set()
                      let cur = byId[id]
                      let guard = 0
                      while (cur && cur.id != null && !seen.has(cur.id) && guard++ < 50) {
                        seen.add(cur.id)
                        if (cur.name) names.push(cur.name)
                        cur = cur.parentOrgId != null ? byId[cur.parentOrgId] : null
                      }
                      return names.reverse().join(' · ')
                    }

                    const l1Id = orgL1 ? String(orgL1) : null
                    if (!l1Id) return null

                    if (orgCategory === 'PERSONAL') {
                      const personalRoot = byId[l1Id]
                      if (!personalRoot) return null
                      return (
                        <option key={String(personalRoot.id)} value={String(personalRoot.id)}>
                          {personalRoot.name}
                        </option>
                      )
                    }

                    const queue = [...(parentToChildren.get(l1Id) || [])]
                    const out = []
                    const seen = new Set()
                    while (queue.length > 0) {
                      const o = queue.shift()
                      if (!o || o.id == null) continue
                      const id = String(o.id)
                      if (seen.has(id)) continue
                      seen.add(id)
                      const typeOk = orgCategory === 'GA'
                        ? (String(o.type).toUpperCase() === 'GA')
                        : (String(o.type).toUpperCase() === 'INSURER')
                      if (typeOk) out.push(o)
                      const kids = parentToChildren.get(id) || []
                      queue.push(...kids)
                    }

                    const sorted = out.sort((a, b) => makePath(a.id).localeCompare(makePath(b.id)))
                    return sorted.map((o) => (
                      <option key={String(o.id)} value={String(o.id)}>
                        {makePath(o.id)}
                      </option>
                    ))
                  })()}
                </select>
                </>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm text-slate-600">이름</label>
              <input className="mt-1 w-full rounded border px-3 py-2" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>

            <div>
              <label className="block text-sm text-slate-600">휴대폰 번호</label>
              <input className="mt-1 w-full rounded border px-3 py-2" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} placeholder="예: 010-1234-5678" required />
            </div>

            <div>
              <label className="block text-sm text-slate-600">이메일(아이디)</label>
              <input className="mt-1 w-full rounded border px-3 py-2" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>

            <button className="w-full rounded bg-brand-navy text-white py-2 disabled:opacity-50" type="submit" disabled={busy}>
              {busy ? '요청 중…' : '임시 비밀번호 받기'}
            </button>
          </form>

          <div className="flex items-center justify-between text-sm text-slate-600">
            <Link to="/find-id" className="text-brand-navy font-semibold underline">아이디 찾기</Link>
            <Link to="/login" className="text-brand-navy font-semibold underline">로그인</Link>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  )
}

